import React, {useEffect, useRef, useState} from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {gt} from 'app/utils/i18n';

import {useTransferTemplate} from '../api';

interface Props extends FormErrorsProps {
  templateURL: string;
  templateName: string;
  isOpen: boolean;
  icon: string;
  onClose: () => unknown;
}

function BaseTransferTemplateModal({
  templateURL,
  templateName,
  isOpen,
  icon,
  onClose,
  captureFormErrorsFromRejection,
}: Props) {
  const [transferTemplate] = useTransferTemplate();
  const [status, setStatus] = useState<
    'start' | 'transferring' | 'success' | 'error'
  >('start');

  const [name, setName] = useState<string>(templateName);

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current != null && inputRef.current.value !== '') {
      inputRef.current.select();
    }
  }, []);

  function clone(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    setStatus('transferring');

    return transferTemplate({template: templateURL, name})
      .unwrap()
      .then(() => {
        setStatus('success');
        onClose();
      })
      .catch((err) => {
        if (err.status === 400) {
          setStatus('start');
          captureFormErrorsFromRejection(err);
        } else {
          setStatus('error');
        }
        return err;
      });
  }

  return (
    <TabbedModal
      show={isOpen}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Use Template'),
          icon: icon,
          content: (
            <form onSubmit={clone}>
              {status === 'start' ? (
                <FormControlGroup field="name">
                  <label htmlFor="transferred-document-name">
                    {gt('Document Name')}
                  </label>
                  <input
                    ref={inputRef}
                    placeholder={gt('Untitled Document')}
                    id="transferred-template-name"
                    className="form-control"
                    autoFocus={true}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControlGroup>
              ) : status === 'transferring' ? (
                <div className="text-center">
                  <div className="progress my-3">
                    <div className="px-1 progress-bar progress-bar-striped progress-bar-animated w-100">
                      {gt('Transferring Template')}
                    </div>
                  </div>
                  <p>
                    {gt(
                      'This could take some seconds, your document will appear on documents dashboard when finished.'
                    )}
                  </p>
                </div>
              ) : status === 'success' ? (
                <div className="text-center">
                  <p className="mb-1">
                    {gt('The document based on the template has been created')}
                  </p>
                </div>
              ) : status === 'error' ? (
                <div className="text-danger text-center">
                  <p className="font-weight-bold">
                    {gt('There was an error transferring the template.')}
                  </p>
                  <p>{gt('Please try again later')}.</p>
                </div>
              ) : null}

              <StyledModalFooter>
                <button
                  onClick={onClose}
                  type="button"
                  className="btn btn-link text-secondary"
                >
                  <i className="fa fa-times mr-1" />
                  {status === 'start' ? gt('Cancel') : gt('Close')}
                </button>
                {status === 'start' && (
                  <LazyTooltip
                    tooltip={gt('Create a document based on this template')}
                  >
                    <button
                      disabled={name.length === 0}
                      className="btn btn-primary"
                      type="submit"
                    >
                      {gt('Create Document')}
                    </button>
                  </LazyTooltip>
                )}
              </StyledModalFooter>
            </form>
          ),
        },
      ]}
    />
  );
}

export const TransferTemplateModal = createFormErrorsContext<Props>(
  BaseTransferTemplateModal
);
